import type { KeyboardEvent, ClipboardEvent } from 'react';
import content from '../../../content';
import type { Question, Rules } from '../../../config/types';

const digitsOnlyPasteRegExp = /^[1234567890]*$/;
const floatPasteRegExp = /^-?(\d*(\.\d*)?)?$/;

type NumericInputOptions = {
  allowFloat?: boolean;
  decimalPlaces?: number;
};

const getPastePattern = (options?: NumericInputOptions) => {
  if (options?.allowFloat && typeof options.decimalPlaces === 'number' && options.decimalPlaces >= 0) {
    return new RegExp(`^-?(\\d*(\\.\\d{0,${options.decimalPlaces}})?)?$`);
  }
  return options?.allowFloat ? floatPasteRegExp : digitsOnlyPasteRegExp;
};

const getAllowedPasteCharacterPattern = (options?: NumericInputOptions) =>
  options?.allowFloat ? /[0-9.-]/ : /[0-9]/;

export function sanitizeNumericPaste(
  currentValue: string,
  pastedValue: string,
  selectionStart: number,
  selectionEnd: number,
  options?: NumericInputOptions,
) {
  const prefix = currentValue.slice(0, selectionStart);
  const suffix = currentValue.slice(selectionEnd);
  const pattern = getPastePattern(options);
  const allowedCharacterPattern = getAllowedPasteCharacterPattern(options);

  let acceptedPaste = '';

  for (const char of pastedValue) {
    if (!allowedCharacterPattern.test(char)) {
      continue;
    }

    const candidatePaste = `${acceptedPaste}${char}`;
    const candidateValue = `${prefix}${candidatePaste}${suffix}`;

    if (pattern.test(candidateValue)) {
      acceptedPaste = candidatePaste;
    }
  }

  return {
    nextValue: `${prefix}${acceptedPaste}${suffix}`,
    acceptedPaste,
  };
}

export function parseDecimalPlacesRule(value: unknown): number | undefined {
  if (typeof value !== 'string') return undefined;
  const match = value.trim().match(/^decimal\((\d+)\)$/i);
  if (!match) return undefined;
  const places = Number.parseInt(match[1], 10);
  return Number.isNaN(places) ? undefined : places;
}

export function handleKeyPress(
  e: KeyboardEvent<HTMLInputElement>,
  options?: NumericInputOptions,
) {
  if (options?.allowFloat) {
    const controlKeys = new Set([
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Delete',
      'Home',
      'End',
    ]);

    if (e.ctrlKey || e.metaKey || e.altKey || controlKeys.has(e.key)) {
      return;
    }

    const { value } = e.currentTarget;
    const selectionStart = e.currentTarget.selectionStart ?? 0;
    const selectionEnd = e.currentTarget.selectionEnd ?? selectionStart;

    if (e.key === '-') {
      const hasMinus = value.includes('-');
      const selectionContainsMinus = value.slice(selectionStart, selectionEnd).includes('-');
      if ((hasMinus && !selectionContainsMinus) || selectionStart !== 0) {
        e.preventDefault();
      }
      return;
    }

    if (e.key === '.') {
      const hasDecimal = value.includes('.');
      const selectionContainsDecimal = value.slice(selectionStart, selectionEnd).includes('.');
      if (hasDecimal && !selectionContainsDecimal) {
        e.preventDefault();
      }
      return;
    }

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    if (typeof options?.decimalPlaces === 'number' && options.decimalPlaces >= 0) {
      const decimalIndex = value.indexOf('.');
      if (decimalIndex >= 0 && selectionStart > decimalIndex) {
        const currentDecimals = value.slice(decimalIndex + 1);
        const selectedDecimals = value
          .slice(selectionStart, selectionEnd)
          .replace(/[^0-9]/g, '').length;
        const maxDecimals = options.decimalPlaces;
        if (currentDecimals.length - selectedDecimals >= maxDecimals) {
          e.preventDefault();
        }
      }
    }
    return;
  }

  // Default behavior: allow digits only
  if ('.-+eE'.includes(e.key)) {
    e.preventDefault();
  }
}

export function handlePaste(e: ClipboardEvent<HTMLInputElement>, options?: NumericInputOptions) {
  const target = e.currentTarget;
  const selectionStart = target.selectionStart ?? 0;
  const selectionEnd = target.selectionEnd ?? selectionStart;
  const { nextValue, acceptedPaste } = sanitizeNumericPaste(
    target.value,
    e.clipboardData.getData('text'),
    selectionStart,
    selectionEnd,
    options,
  );

  e.preventDefault();

  if (nextValue === target.value) {
    return;
  }

  const valueSetter =
    Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;

  valueSetter?.call(target, nextValue);
  target.dispatchEvent(new Event('input', { bubbles: true }));

  requestAnimationFrame(() => {
    try {
      const nextCursor = selectionStart + acceptedPaste.length;
      target.setSelectionRange(nextCursor, nextCursor);
    } catch (_error) {
      // Number inputs do not always support setting the selection range.
    }
  });
}

export function invalidDateErrorMessage(question: Question): string {
  return String(content.invalidDate).replace('$LABEL', question.title);
}

export function dateIsFullyPopulated(value: string) {
  if (!value || value.indexOf('-') < 0) {
    return false;
  }
  const values = value.split('-');
  if (values.length === 3) {
    return values[0].length > 0 && values[1].length > 0 && values[2].length > 0;
  }
  return values[0].length > 0 && values[1].length > 0;
}

export function dateIsEmpty(value: string) {
  return !value || value === '-' || value === '--';
}

export function getMinYearValue(rules: Rules): number {
  let minYearInt = Number.parseInt(rules.minYear ?? '', 10);
  if (Number.isNaN(minYearInt)) {
    minYearInt = 1900;
  }
  return minYearInt;
}

export function isMinYearRuleValid(rules: Rules, value: string): boolean {
  if (dateIsFullyPopulated(value)) {
    const minYearInt = getMinYearValue(rules);
    const values = value.split('-');
    const yearInt = Number.parseInt(values[0], 10);
    if (!Number.isNaN(yearInt) && yearInt < minYearInt) {
      return false;
    }
  }
  return true;
}

export function isValidDate(value: string) {
  const values = value.split('-');
  if (values.length < 2 || values.length > 3) {
    return false;
  }
  const year = Number.parseInt(values[0], 10);
  const month = Number.parseInt(values[1], 10);
  // For short dates default the day to the 1st, will be valid for all months
  const day = values.length === 3 ? Number.parseInt(values[2], 10) : 1;
  const toCheck = new Date(year, month - 1, day);
  if (Number.isNaN(toCheck)) {
    return false;
  }
  // Javascript allows dates like 2000-02-31, they evaluate to 2000-03-02
  // Just check the month hasn't rolled over.
  return month === toCheck.getMonth() + 1;
}
